/**
 * Script Widget sandbox runtime — Phase L4b.
 *
 * One iframe per LayoutRenderer. The iframe is loaded with `srcdoc` and
 * `sandbox="allow-scripts"` (no `allow-same-origin`) — meaning customer-authored
 * JS cannot read this origin's cookies, can't reach the backend on its own,
 * and can't touch the parent DOM. All side-effects round-trip to the host frame
 * over postMessage.
 *
 * Two kinds of invocations:
 *   - kind: 'script'    — Script Widget body, fire-and-forget result.
 *   - kind: 'transform' — Wire payload-mapping snippet, returns a new payload.
 *
 * Bridge protocol (host ⇄ sandbox):
 *   host → sandbox  invoke           {type:'invoke', invocation_id, kind, source, payload, ctx, timeout_ms}
 *   sandbox → host  result           {type:'result', invocation_id, ok, value, error}
 *   sandbox → host  bus_publish      {type:'bus_publish', topic, payload, source_widget_id}
 *   sandbox → host  invoke_action    {type:'invoke_action', widget_id, action, payload}
 *   sandbox → host  fetch_request    {type:'fetch_request', request_id, url, method, headers, body, timeout_ms}
 *   host → sandbox  fetch_response   {type:'fetch_response', request_id, ok, status, headers, text, json, error}
 *   sandbox → host  log              {type:'log', level, args, source_widget_id}
 */

const SANDBOX_HTML = `<!doctype html>
<html><head><meta charset="utf-8"></head><body><script>
(function(){
  var pendingFetches = Object.create(null);
  var fetchSeq = 0;
  // Cached compilation: source → AsyncFunction
  var compileCache = Object.create(null);

  function compile(source, kind) {
    if (compileCache[source]) return compileCache[source];
    try {
      var fn;
      if (kind === 'transform') {
        // Transforms are expressions OR statement bodies returning a value.
        // We wrap as: function(payload, ctx) { <body> } — admins can write
        // either "return {...}" or just an object literal preceded by "return".
        fn = new Function('payload', 'ctx', 'helpers', 'return (async () => { ' + source + ' })();');
      } else {
        fn = new Function('payload', 'ctx', 'helpers', 'return (async () => { ' + source + ' })();');
      }
      compileCache[source] = fn;
      return fn;
    } catch (err) {
      throw new Error('Compile error: ' + (err && err.message ? err.message : String(err)));
    }
  }

  function buildCtx(rawCtx, sourceWidgetId) {
    var ctx = Object.assign({}, rawCtx || {});
    ctx.log = {
      info: function () { var a = Array.prototype.slice.call(arguments); parent.postMessage({ type: 'log', level: 'info', args: serialize(a), source_widget_id: sourceWidgetId }, '*'); },
      warn: function () { var a = Array.prototype.slice.call(arguments); parent.postMessage({ type: 'log', level: 'warn', args: serialize(a), source_widget_id: sourceWidgetId }, '*'); },
      error: function () { var a = Array.prototype.slice.call(arguments); parent.postMessage({ type: 'log', level: 'error', args: serialize(a), source_widget_id: sourceWidgetId }, '*'); }
    };
    ctx.bus = ctx.bus || {};
    ctx.bus.publish = function (topic, payload) {
      parent.postMessage({ type: 'bus_publish', topic: topic, payload: payload == null ? null : payload, source_widget_id: sourceWidgetId }, '*');
    };
    ctx.invokeAction = function (widgetId, action, payload) {
      parent.postMessage({ type: 'invoke_action', widget_id: widgetId, action: action, payload: payload == null ? null : payload }, '*');
    };
    ctx.fetch = function (url, opts) {
      opts = opts || {};
      var rid = 'f' + (++fetchSeq);
      return new Promise(function (resolve, reject) {
        pendingFetches[rid] = { resolve: resolve, reject: reject };
        parent.postMessage({
          type: 'fetch_request',
          request_id: rid,
          url: String(url),
          method: opts.method || 'GET',
          headers: opts.headers || null,
          body: opts.body == null ? null : opts.body,
          timeout_ms: opts.timeout_ms || null
        }, '*');
      });
    };
    return ctx;
  }

  function serialize(arr) {
    try { return JSON.parse(JSON.stringify(arr)); } catch (e) { return arr.map(function(x){ try{return String(x);}catch(_){return '?';}}); }
  }

  var helpers = {};

  window.addEventListener('message', function (e) {
    var msg = e.data || {};
    if (!msg || typeof msg !== 'object') return;
    if (msg.type === 'helpers_init') {
      try { helpers = msg.helpers || {}; } catch (_) {}
      return;
    }
    if (msg.type === 'fetch_response') {
      var p = pendingFetches[msg.request_id];
      if (!p) return;
      delete pendingFetches[msg.request_id];
      if (msg.error) p.reject(new Error(msg.error));
      else p.resolve({ ok: !!msg.ok, status: msg.status, headers: msg.headers || {}, text: msg.text || '', json: msg.json || null });
      return;
    }
    if (msg.type === 'invoke') {
      var invocationId = msg.invocation_id;
      var fn;
      try { fn = compile(msg.source, msg.kind); }
      catch (err) {
        parent.postMessage({ type: 'result', invocation_id: invocationId, ok: false, error: err.message || String(err) }, '*');
        return;
      }
      var ctx = buildCtx(msg.ctx, msg.source_widget_id);
      Promise.resolve()
        .then(function () { return fn(msg.payload == null ? null : msg.payload, ctx, helpers); })
        .then(function (value) {
          var safeValue;
          try { safeValue = JSON.parse(JSON.stringify(value == null ? null : value)); } catch (_) { safeValue = null; }
          parent.postMessage({ type: 'result', invocation_id: invocationId, ok: true, value: safeValue }, '*');
        })
        .catch(function (err) {
          parent.postMessage({ type: 'result', invocation_id: invocationId, ok: false, error: err && err.message ? err.message : String(err) }, '*');
        });
      return;
    }
  });

  parent.postMessage({ type: 'sandbox_ready' }, '*');
})();
</`+`script></body></html>`;

const DEFAULT_INVOKE_TIMEOUT_MS = 5000;

export function createScriptSandbox({ onBusPublish, onInvokeAction, onFetchRequest, onLog } = {}) {
  let iframe = null;
  let ready = false;
  const readyWaiters = [];
  const pending = new Map();        // invocation_id → { resolve, reject, timer }
  let invocationSeq = 0;
  let destroyed = false;

  const handler = (e) => {
    if (!iframe || e.source !== iframe.contentWindow) return;
    const msg = e.data || {};
    const type = msg && msg.type;
    if (type === 'sandbox_ready') {
      ready = true;
      readyWaiters.splice(0).forEach((r) => r());
      return;
    }
    if (type === 'result') {
      const entry = pending.get(msg.invocation_id);
      if (!entry) return;
      pending.delete(msg.invocation_id);
      clearTimeout(entry.timer);
      if (msg.ok) entry.resolve(msg.value);
      else entry.reject(new Error(msg.error || 'Script error'));
      return;
    }
    if (type === 'bus_publish') {
      try { onBusPublish && onBusPublish(msg.topic, msg.payload, msg.source_widget_id); } catch (_) { /* swallow */ }
      return;
    }
    if (type === 'invoke_action') {
      try { onInvokeAction && onInvokeAction(msg.widget_id, msg.action, msg.payload); } catch (_) { /* swallow */ }
      return;
    }
    if (type === 'fetch_request') {
      const reply = (resp) => {
        if (!iframe || destroyed) return;
        try { iframe.contentWindow.postMessage({ type: 'fetch_response', request_id: msg.request_id, ...resp }, '*'); } catch (_) { /* swallow */ }
      };
      if (onFetchRequest) {
        Promise.resolve().then(() => onFetchRequest(msg)).then(
          (resp) => reply(resp || { ok: false, error: 'fetch returned no response' }),
          (err) => reply({ ok: false, error: err && err.message ? err.message : String(err) }),
        );
      } else {
        reply({ ok: false, error: 'No fetch handler registered.' });
      }
      return;
    }
    if (type === 'log') {
      try { onLog && onLog(msg.level, msg.args, msg.source_widget_id); } catch (_) { /* swallow */ }
      return;
    }
  };

  function ensureIframe() {
    if (iframe && !destroyed) return;
    if (typeof document === 'undefined') return;
    iframe = document.createElement('iframe');
    iframe.setAttribute('sandbox', 'allow-scripts');
    iframe.setAttribute('srcdoc', SANDBOX_HTML);
    iframe.style.display = 'none';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.title = 'casematrix-script-sandbox';
    document.body.appendChild(iframe);
    window.addEventListener('message', handler);
  }

  function whenReady() {
    if (ready) return Promise.resolve();
    return new Promise((resolve) => readyWaiters.push(resolve));
  }

  ensureIframe();

  async function invoke(kind, source, payload, ctxData, opts) {
    if (destroyed) throw new Error('Sandbox destroyed');
    if (typeof source !== 'string' || !source.trim()) throw new Error('Empty script source');
    await whenReady();
    const invocationId = 'inv' + (++invocationSeq);
    const timeoutMs = (opts && opts.timeout_ms) || DEFAULT_INVOKE_TIMEOUT_MS;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (!pending.has(invocationId)) return;
        pending.delete(invocationId);
        // Hard-kill: a wedged iframe blocks all subsequent invocations.
        try { destroy(); } catch (_) { /* ignore */ }
        try { ensureIframe(); ready = false; } catch (_) { /* ignore */ }
        reject(new Error('Script timed out after ' + timeoutMs + 'ms'));
      }, timeoutMs);
      pending.set(invocationId, { resolve, reject, timer });
      try {
        iframe.contentWindow.postMessage({
          type: 'invoke',
          invocation_id: invocationId,
          kind,
          source,
          payload: payload == null ? null : payload,
          ctx: ctxData || {},
          source_widget_id: (opts && opts.source_widget_id) || null,
          timeout_ms: timeoutMs,
        }, '*');
      } catch (err) {
        pending.delete(invocationId);
        clearTimeout(timer);
        reject(err);
      }
    });
  }

  function setHelpers(helpersObj) {
    whenReady().then(() => {
      try {
        let safe;
        try { safe = JSON.parse(JSON.stringify(helpersObj || {})); } catch (_) { safe = {}; }
        iframe.contentWindow.postMessage({ type: 'helpers_init', helpers: safe }, '*');
      } catch (_) { /* swallow */ }
    });
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    window.removeEventListener('message', handler);
    pending.forEach(({ reject, timer }) => { clearTimeout(timer); try { reject(new Error('Sandbox destroyed')); } catch (_) { /* swallow */ } });
    pending.clear();
    if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
    iframe = null;
    ready = false;
  }

  return { invoke, setHelpers, destroy };
}
